import * as THREE from 'three'
import { ARENA_RADIUS, COLORS } from '../config'
import {
  clipSegmentToObstacles,
  hasLineOfSight as queryLineOfSight,
  resolveBodyAgainstObstacles,
  type SpatialObstacle,
} from './spatial'

function seeded(seed: number): () => number {
  let value = seed >>> 0
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0
    return value / 0xffffffff
  }
}

export class Arena {
  readonly root = new THREE.Group()
  readonly obstacles: SpatialObstacle[] = []
  private readonly dust: THREE.Points
  private structureBatches = 0
  private structureInstances = 0
  private decorBatches = 0
  private decorInstances = 0

  constructor(scene: THREE.Scene) {
    scene.add(this.root)
    this.addSky(scene)
    this.addLights(scene)
    this.addGround()
    this.addStructures()
    this.addMidfieldDetail()
    this.addHorizonSilhouette()
    this.addDustFields()
    this.dust = this.createAirborneDust()
    scene.add(this.dust)
  }

  update(time: number): void {
    this.dust.rotation.y = time * 0.006
    const material = this.dust.material as THREE.PointsMaterial
    material.opacity = 0.18 + Math.sin(time * 0.18) * 0.035
  }

  clampToArena(position: THREE.Vector3, padding = 0): boolean {
    const radius = Math.hypot(position.x, position.z)
    const limit = Math.max(0, ARENA_RADIUS - Math.max(0, padding))
    if (radius > limit) {
      const scale = limit / radius
      position.x *= scale
      position.z *= scale
      return true
    }
    return false
  }

  resolveBody(
    position: THREE.Vector3,
    radius: number,
    height: number,
    previousPosition?: THREE.Vector3,
  ): boolean {
    let collided = this.clampToArena(position, radius)
    const resolution = resolveBodyAgainstObstacles(position, radius, height, this.obstacles)
    position.set(resolution.position.x, resolution.position.y, resolution.position.z)
    collided = resolution.collided || collided
    collided = this.clampToArena(position, radius) || collided
    if (!this.bodyOverlapsObstacle(position, radius, height)) return collided

    if (previousPosition) {
      const fallback = previousPosition.clone()
      this.clampToArena(fallback, radius)
      if (!this.bodyOverlapsObstacle(fallback, radius, height)) {
        position.copy(fallback)
        return true
      }
    }

    // If a teleport or spawn supplied no safe prior point, search inward from
    // the constrained edge. The arena centre is intentionally structure-free.
    const origin = position.clone()
    const inward = new THREE.Vector3(-origin.x, 0, -origin.z)
    if (inward.lengthSq() > 0) inward.normalize()
    else inward.set(1, 0, 0)
    for (let distance = 0.5; distance <= ARENA_RADIUS * 2; distance += 0.5) {
      const candidate = origin.clone().addScaledVector(inward, distance)
      this.clampToArena(candidate, radius)
      if (this.bodyOverlapsObstacle(candidate, radius, height)) continue
      position.copy(candidate)
      return true
    }

    position.set(0, position.y, 0)
    return true
  }

  hasLineOfSight(start: THREE.Vector3, end: THREE.Vector3, clearance = 0): boolean {
    return queryLineOfSight(start, end, this.obstacles, clearance)
  }

  traceLine(
    start: THREE.Vector3,
    end: THREE.Vector3,
    clearance = 0,
    padding = 0,
  ): { point: THREE.Vector3; blocked: boolean; obstacleId: string | null } {
    const trace = clipSegmentToObstacles(start, end, this.obstacles, clearance, padding)
    return {
      point: new THREE.Vector3(trace.point.x, trace.point.y, trace.point.z),
      blocked: trace.blocked,
      obstacleId: trace.obstacleId,
    }
  }

  /** Batching of the collidable perimeter only. Scenery is counted separately. */
  structureBatchStats(): { batches: number; instances: number } {
    return { batches: this.structureBatches, instances: this.structureInstances }
  }

  /** Batching of non-collidable scenery: midfield clutter and the horizon ring. */
  decorBatchStats(): { batches: number; instances: number } {
    return { batches: this.decorBatches, instances: this.decorInstances }
  }

  private bodyOverlapsObstacle(position: THREE.Vector3, radius: number, height: number): boolean {
    return resolveBodyAgainstObstacles(position, radius, height, this.obstacles).collided
  }

  private addSky(scene: THREE.Scene): void {
    scene.background = new THREE.Color(0x05080b)
    scene.fog = new THREE.FogExp2(0x1a2023, 0.0062)
    const geometry = new THREE.SphereGeometry(390, 48, 24)
    const material = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor: { value: new THREE.Color(0x07121c) },
        horizonColor: { value: new THREE.Color(0x7a3727) },
        bottomColor: { value: new THREE.Color(0x0a0b0c) },
      },
      vertexShader: `
        varying vec3 vWorld;
        void main() {
          vec4 world = modelMatrix * vec4(position, 1.0);
          vWorld = normalize(world.xyz);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      // The previous cloud term hashed a quantised grid, which aliased into
      // hard diagonal shards near the horizon and read as a broken shader
      // rather than as weather. Smooth value noise in two octaves, stretched
      // horizontally, gives banded dust instead.
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 horizonColor;
        uniform vec3 bottomColor;
        varying vec3 vWorld;

        float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

        float valueNoise(vec2 p) {
          vec2 cell = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);
          float a = hash(cell);
          float b = hash(cell + vec2(1.0, 0.0));
          float c = hash(cell + vec2(0.0, 1.0));
          float d = hash(cell + vec2(1.0, 1.0));
          return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
        }

        void main() {
          float y = vWorld.y;
          vec3 color = y > 0.0
            ? mix(horizonColor, topColor, smoothstep(0.0, 0.75, y))
            : mix(horizonColor, bottomColor, smoothstep(0.0, -0.45, y));

          // Wide in azimuth, tight in elevation, so the noise lies down as
          // strata the way wind-carried dust does.
          vec2 sky = vec2(atan(vWorld.z, vWorld.x) * 2.4, y * 7.0);
          float dust = valueNoise(sky) * 0.65 + valueNoise(sky * 2.7) * 0.35;
          float band = smoothstep(0.42, 0.86, dust) * (1.0 - smoothstep(0.0, 0.62, abs(y)));
          color += vec3(0.16, 0.085, 0.05) * band;

          // A brighter wash sitting on the horizon line itself.
          color += vec3(0.1, 0.045, 0.022) * (1.0 - smoothstep(0.0, 0.16, abs(y)));
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    })
    scene.add(new THREE.Mesh(geometry, material))

    const sun = new THREE.Mesh(
      new THREE.CircleGeometry(13, 48),
      new THREE.MeshBasicMaterial({ color: 0xff8b51, toneMapped: false }),
    )
    sun.position.set(-125, 58, -280)
    scene.add(sun)
  }

  private addLights(scene: THREE.Scene): void {
    const hemisphere = new THREE.HemisphereLight(0x83a3c0, 0x4a1d14, 1.7)
    scene.add(hemisphere)
    const sun = new THREE.DirectionalLight(0xffc0a0, 4.4)
    sun.position.set(-55, 82, 38)
    sun.castShadow = true
    sun.shadow.mapSize.set(2048, 2048)
    sun.shadow.camera.left = -105
    sun.shadow.camera.right = 105
    sun.shadow.camera.top = 105
    sun.shadow.camera.bottom = -105
    sun.shadow.camera.near = 1
    sun.shadow.camera.far = 220
    sun.shadow.bias = -0.00025
    scene.add(sun)
  }

  private addGround(): void {
    const geometry = new THREE.PlaneGeometry(420, 420, 120, 120)
    const positions = geometry.attributes.position
    if (positions) {
      for (let index = 0; index < positions.count; index += 1) {
        const x = positions.getX(index)
        const y = positions.getY(index)
        const radius = Math.hypot(x, y)
        const edge = THREE.MathUtils.smoothstep(radius, 78, 205)
        const height =
          edge * (Math.sin(x * 0.055) * 4.8 + Math.cos(y * 0.073) * 3.1) +
          edge * edge * 12
        positions.setZ(index, height)
      }
      positions.needsUpdate = true
      geometry.computeVertexNormals()
    }
    const material = new THREE.MeshStandardMaterial({
      color: 0x4a3a33,
      metalness: 0.08,
      roughness: 0.94,
    })
    const ground = new THREE.Mesh(geometry, material)
    ground.rotation.x = -Math.PI / 2
    ground.receiveShadow = true
    this.root.add(ground)

    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(35, 38, 0.7, 12),
      new THREE.MeshStandardMaterial({ color: 0x2c3439, metalness: 0.42, roughness: 0.6 }),
    )
    platform.position.y = -0.15
    platform.receiveShadow = true
    this.root.add(platform)

    for (const radius of [22, 36, 82]) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(radius, radius === 82 ? 0.2 : 0.08, 4, 128),
        new THREE.MeshBasicMaterial({ color: radius === 82 ? 0xa74428 : 0x45575a, transparent: true, opacity: 0.5 }),
      )
      ring.rotation.x = Math.PI / 2
      ring.position.y = 0.25
      this.root.add(ring)
    }
  }

  private addStructures(): void {
    const random = seeded(1649)
    const structureMaterial = new THREE.MeshStandardMaterial({
      color: 0x2e373c,
      metalness: 0.44,
      roughness: 0.58,
    })
    const rustMaterial = new THREE.MeshStandardMaterial({
      color: 0x6b3a2c,
      metalness: 0.34,
      roughness: 0.72,
    })
    const glowMaterial = new THREE.MeshBasicMaterial({ color: COLORS.ember, toneMapped: false })
    const structureBoxes: THREE.Matrix4[] = []
    const rustBoxes: THREE.Matrix4[] = []
    const glowBoxes: THREE.Matrix4[] = []
    const antennas: THREE.Matrix4[] = []
    const beacons: THREE.Matrix4[] = []

    for (let index = 0; index < 42; index += 1) {
      const angle = (index / 42) * Math.PI * 2 + (random() - 0.5) * 0.08
      const radius = 88 + random() * 74
      const width = 5 + random() * 13
      const depth = 5 + random() * 12
      const height = 12 + random() ** 2 * 55
      const centerX = Math.sin(angle) * radius
      const centerZ = Math.cos(angle) * radius
      const yaw = -angle + (random() - 0.5) * 0.28
      const coreMatrix = composeInstance(centerX, height / 2, centerZ, yaw, width, height, depth)
      if (index % 4 === 0) rustBoxes.push(coreMatrix)
      else structureBoxes.push(coreMatrix)
      this.obstacles.push({
        id: `perimeter-${index}-core`,
        centerX,
        centerZ,
        halfWidth: width / 2,
        halfDepth: depth / 2,
        yaw,
        minY: 0,
        maxY: height,
      })

      if (index % 3 === 0) {
        const crownY = height * 0.78
        structureBoxes.push(
          composeInstance(centerX, crownY, centerZ, yaw, width * 1.45, 1.2, depth * 1.35),
        )
        this.obstacles.push({
          id: `perimeter-${index}-crown`,
          centerX,
          centerZ,
          halfWidth: (width * 1.45) / 2,
          halfDepth: (depth * 1.35) / 2,
          yaw,
          minY: crownY - 0.6,
          maxY: crownY + 0.6,
        })
      }
      if (index % 5 === 0) {
        antennas.push(composeInstance(centerX, height + 6, centerZ, 0, 1, 1, 1))
        beacons.push(composeInstance(centerX, height + 12, centerZ, 0, 1, 1, 1))
      }
    }

    for (let index = 0; index < 9; index += 1) {
      const angle = (index / 9) * Math.PI * 2
      const pillarX = Math.sin(angle) * 68
      const pillarZ = Math.cos(angle) * 68
      structureBoxes.push(composeInstance(pillarX, 9, pillarZ, -angle, 3.8, 18, 5.2))
      this.obstacles.push({
        id: `inner-pillar-${index}`,
        centerX: pillarX,
        centerZ: pillarZ,
        halfWidth: 1.9,
        halfDepth: 2.6,
        yaw: -angle,
        minY: 0,
        maxY: 18,
      })
      glowBoxes.push(
        composeInstance(
          Math.sin(angle) * 65.95,
          11,
          Math.cos(angle) * 65.95,
          -angle,
          0.18,
          10,
          5.24,
        ),
      )
    }

    const boxGeometry = new THREE.BoxGeometry(1, 1, 1)
    this.addStructureBatch(boxGeometry, structureMaterial, structureBoxes, true, true)
    this.addStructureBatch(boxGeometry, rustMaterial, rustBoxes, true, true)
    this.addStructureBatch(boxGeometry, glowMaterial, glowBoxes, false, false)
    this.addStructureBatch(
      new THREE.CylinderGeometry(0.18, 0.38, 12, 6),
      structureMaterial,
      antennas,
      false,
      false,
    )
    this.addStructureBatch(
      new THREE.SphereGeometry(0.32, 8, 6),
      glowMaterial,
      beacons,
      false,
      false,
    )
  }

  /**
   * @param bucket Which budget this batch counts against. `structure` is the
   *   load-bearing perimeter, whose batching is asserted against a fixed
   *   count; `decor` is scenery that carries no collider. Keeping them apart
   *   means adding scenery cannot quietly satisfy the structure assertion.
   */
  private addStructureBatch(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    matrices: readonly THREE.Matrix4[],
    castShadow: boolean,
    receiveShadow: boolean,
    bucket: 'structure' | 'decor' = 'structure',
  ): void {
    if (matrices.length === 0) return
    const batch = new THREE.InstancedMesh(geometry, material, matrices.length)
    matrices.forEach((matrix, index) => batch.setMatrixAt(index, matrix))
    batch.instanceMatrix.setUsage(THREE.StaticDrawUsage)
    batch.castShadow = castShadow
    batch.receiveShadow = receiveShadow
    batch.computeBoundingBox()
    batch.computeBoundingSphere()
    this.root.add(batch)
    if (bucket === 'structure') {
      this.structureBatches += 1
      this.structureInstances += matrices.length
    } else {
      this.decorBatches += 1
      this.decorInstances += matrices.length
    }
  }

  /**
   * Decoration for the empty ring between the combat platform and the
   * perimeter structures.
   *
   * Nothing here registers an obstacle. `resolveBody` depends on the centre of
   * the arena staying structure-free as its last-resort escape, and the shared
   * collision set is pinned at 65 by both the unit tests and the browser
   * scenario. So every piece is either flat on the deck or short enough to
   * read as something a mech steps over.
   */
  private addMidfieldDetail(): void {
    const random = seeded(4211)
    // Close to the platform's own 0x2c3439: decking should separate from the
    // pad by its seams and its specular, not by being a lighter colour.
    const deckMaterial = new THREE.MeshStandardMaterial({
      color: 0x333d43,
      metalness: 0.4,
      roughness: 0.62,
    })
    const ventMaterial = new THREE.MeshStandardMaterial({
      color: 0x3f4a50,
      metalness: 0.38,
      roughness: 0.64,
    })
    const conduitMaterial = new THREE.MeshStandardMaterial({
      color: 0x59463c,
      metalness: 0.3,
      roughness: 0.78,
    })
    const hazardMaterial = new THREE.MeshBasicMaterial({
      color: 0xc9682f,
      transparent: true,
      opacity: 0.55,
    })

    const deckPlates: THREE.Matrix4[] = []
    const conduits: THREE.Matrix4[] = []
    const vents: THREE.Matrix4[] = []
    const hazardStripes: THREE.Matrix4[] = []

    // Plating across the landing pad, so the platform reads as built rather
    // than as a flat disc. Laid on a polar grid and aligned outward: scattered
    // at random angles the same plates read as loose sheets dropped on the
    // deck rather than as decking bolted into it.
    const RINGS = [
      { radius: 11, sectors: 10 },
      { radius: 19, sectors: 14 },
      { radius: 27, sectors: 18 },
    ]
    for (const ring of RINGS) {
      const arc = (Math.PI * 2) / ring.sectors
      for (let sector = 0; sector < ring.sectors; sector += 1) {
        // A few missing panels read as wear and stop the grid looking printed.
        if (random() < 0.16) continue
        const angle = sector * arc + arc * 0.5
        deckPlates.push(
          composeInstance(
            Math.sin(angle) * ring.radius,
            0.22,
            Math.cos(angle) * ring.radius,
            -angle,
            ring.radius * arc * 0.86,
            0.16,
            6.4,
          ),
        )
      }
    }

    // Cable and pipe runs spoking out from the pad towards the perimeter.
    for (let index = 0; index < 34; index += 1) {
      const angle = (index / 34) * Math.PI * 2 + (random() - 0.5) * 0.3
      const inner = 34 + random() * 8
      const length = 16 + random() * 26
      const radius = inner + length / 2
      conduits.push(
        composeInstance(
          Math.sin(angle) * radius,
          0.42 + random() * 0.3,
          Math.cos(angle) * radius,
          -angle,
          0.55 + random() * 0.7,
          0.55,
          length,
        ),
      )
    }

    // Low vent stacks and bollards: the height cue that makes the midfield
    // read as ground rather than as backdrop.
    for (let index = 0; index < 38; index += 1) {
      const angle = random() * Math.PI * 2
      const radius = 40 + random() * 42
      const height = 1.2 + random() ** 2 * 3.4
      vents.push(
        composeInstance(
          Math.sin(angle) * radius,
          height / 2,
          Math.cos(angle) * radius,
          random() * Math.PI,
          1.1 + random() * 1.9,
          height,
          1.1 + random() * 1.9,
        ),
      )
    }

    // Hazard chevrons marking the pad edge.
    for (let index = 0; index < 26; index += 1) {
      const angle = (index / 26) * Math.PI * 2
      hazardStripes.push(
        composeInstance(
          Math.sin(angle) * 31.5,
          0.34,
          Math.cos(angle) * 31.5,
          -angle,
          2.6,
          0.06,
          0.9,
        ),
      )
    }

    const box = new THREE.BoxGeometry(1, 1, 1)
    this.addStructureBatch(box, deckMaterial, deckPlates, false, true, 'decor')
    this.addStructureBatch(box, conduitMaterial, conduits, true, true, 'decor')
    this.addStructureBatch(box, ventMaterial, vents, true, true, 'decor')
    this.addStructureBatch(box, hazardMaterial, hazardStripes, false, false, 'decor')
  }

  /**
   * A second, further, taller ring beyond the perimeter. Purely a depth cue:
   * it sits outside the fog's useful range and never collides.
   */
  private addHorizonSilhouette(): void {
    const random = seeded(8807)
    const material = new THREE.MeshStandardMaterial({
      color: 0x232b30,
      metalness: 0.3,
      roughness: 0.8,
    })
    const towers: THREE.Matrix4[] = []
    for (let index = 0; index < 46; index += 1) {
      const angle = (index / 46) * Math.PI * 2 + (random() - 0.5) * 0.12
      const radius = 205 + random() * 95
      const height = 40 + random() ** 2 * 120
      towers.push(
        composeInstance(
          Math.sin(angle) * radius,
          height / 2,
          Math.cos(angle) * radius,
          -angle + (random() - 0.5) * 0.5,
          9 + random() * 22,
          height,
          9 + random() * 20,
        ),
      )
    }
    this.addStructureBatch(new THREE.BoxGeometry(1, 1, 1), material, towers, false, false, 'decor')
  }

  private addDustFields(): void {
    const random = seeded(771)
    const geometry = new THREE.DodecahedronGeometry(0.7, 0)
    const material = new THREE.MeshStandardMaterial({ color: 0x4c3b35, roughness: 1 })
    const debris = new THREE.InstancedMesh(geometry, material, 260)
    const matrix = new THREE.Matrix4()
    const position = new THREE.Vector3()
    const rotation = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    for (let index = 0; index < 260; index += 1) {
      const radius = 40 + random() * 145
      const angle = random() * Math.PI * 2
      position.set(Math.sin(angle) * radius, random() * 1.2 - 0.2, Math.cos(angle) * radius)
      rotation.setFromEuler(new THREE.Euler(random() * 3, random() * 3, random() * 3))
      const size = 0.15 + random() ** 3 * 2.8
      scale.set(size * 1.8, size * 0.55, size)
      matrix.compose(position, rotation, scale)
      debris.setMatrixAt(index, matrix)
    }
    debris.castShadow = true
    debris.receiveShadow = true
    this.root.add(debris)
  }

  private createAirborneDust(): THREE.Points {
    const random = seeded(90210)
    const values: number[] = []
    for (let index = 0; index < 1_500; index += 1) {
      const radius = 10 + random() * 240
      const angle = random() * Math.PI * 2
      values.push(Math.sin(angle) * radius, 1 + random() * 48, Math.cos(angle) * radius)
    }
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(values, 3))
    const material = new THREE.PointsMaterial({
      color: 0xb06a48,
      size: 0.22,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
    return new THREE.Points(geometry, material)
  }
}

function composeInstance(
  x: number,
  y: number,
  z: number,
  yaw: number,
  scaleX: number,
  scaleY: number,
  scaleZ: number,
): THREE.Matrix4 {
  const position = new THREE.Vector3(x, y, z)
  const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw, 0))
  const scale = new THREE.Vector3(scaleX, scaleY, scaleZ)
  return new THREE.Matrix4().compose(position, rotation, scale)
}
